import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { push } from 'react-router-redux';
import * as BlueprintsSlice from '~/Slices/BlueprintsSlice';
import * as SuppliersSlice from '~/Slices/SuppliersSlice';
import type { IAppState } from '~/appstate';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import { LocalStorage } from '~/helpers';
import type { IToastMessage } from '~/helpers/Toastr';
import { toastr } from '~/helpers/Toastr';
import * as User from '~/helpers/User';
import * as Hub from '~/ogm-hub';

export const onPageLoad = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('massImport/onPageLoad', async (_, { dispatch }) => {
	await dispatch(SuppliersSlice.fetchCurrentCustomerSuppliers());
	await dispatch(fetchMassImportInProgress());

	if (User.isDssiSiteType()) {
		await dispatch(
			BlueprintsSlice.fetchBlueprints({
				supplierFilterId: 0,
				pageNumber: 1,
				searchTerm: '',
				countPerPage: 500,
			})
		);
	}
});

export const fetchMassImportInProgress = createAsyncThunk<
	boolean,
	void,
	{ rejectValue: string[] | Response }
>('massImport/massImportInProgress', async (_, { rejectWithValue }) => {
	try {
		const customerId = LocalStorage.getCustomerId();

		const massImportInProgress = await unwrapFetch2(
			Hub.MassImportRequestController.IsMassImportInProgress(customerId)
		);

		return massImportInProgress;
	} catch (err) {
		if (err instanceof ApiRequestError) {
			return rejectWithValue(err.errors);
		}
		if (err instanceof ApiServerError) {
			return rejectWithValue(err.response);
		}
		throw err;
	}
});

export const fetchBulkApplyTemplateLink = createAsyncThunk<
	string,
	void,
	{ rejectValue: string[] | Response }
>('massImport/bulkApplyTemplateLink', async (_, { rejectWithValue }) => {
	try {
		const bulkApplyTemplateLink = await unwrapFetch2(
			Hub.MassImportRequestController.GetBulkApplyMassImportTemplateDownload()
		);

		return bulkApplyTemplateLink;
	} catch (err) {
		if (err instanceof ApiRequestError) {
			return rejectWithValue(err.errors);
		}
		if (err instanceof ApiServerError) {
			return rejectWithValue(err.response);
		}
		throw err;
	}
});

export interface SubmitMassImportRequestParams {
	file: File;
	supplierId: number;
	successMessage: IToastMessage;
	failureMessage: IToastMessage;
}
export const submitMassImportRequest = createAsyncThunk<
	void,
	SubmitMassImportRequestParams,
	{ rejectValue: string[] | Response }
>(
	'massImport/submitMassImportRequest',
	async (
		params: SubmitMassImportRequestParams,
		{ rejectWithValue, dispatch }
	) => {
		try {
			const formData: FormData = new FormData();
			formData.append('file', params.file);
			formData.append('supplierId', params.supplierId.toString());

			await unwrapFetch2(
				Hub.MassImportRequestController.NewMassImportRequest(formData)
			);

			toastr.success(params.successMessage);
			dispatch(push('/'));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				toastr.error(params.failureMessage);
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				toastr.error(params.failureMessage);
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface SubmitBulkApplyRequestParams {
	file: File;
	blueprintId: number;
	successMessage: IToastMessage;
	failureMessage: IToastMessage;
	applyChanges: boolean;
}
export const submitBulkApplyRequest = createAsyncThunk<
	void,
	SubmitBulkApplyRequestParams,
	{ rejectValue: string[] | Response }
>(
	'massImport/submitBulkApplyRequest',
	async (
		params: SubmitBulkApplyRequestParams,
		{ rejectWithValue, dispatch }
	) => {
		try {
			const formData: FormData = new FormData();
			formData.append('file', params.file);
			formData.append('blueprintId', params.blueprintId.toString());
			formData.append('applyChanges', params.applyChanges.toString());

			await unwrapFetch2(
				Hub.MassImportRequestController.NewBulkApplyMassImportRequest(formData)
			);

			toastr.success(params.successMessage);
			dispatch(push('/'));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				toastr.error(params.failureMessage);
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				toastr.error(params.failureMessage);
				return rejectWithValue(err.response);
			}
			throw err;
		}
		dispatch(massImportSlice.actions.closeBulkApplyConfirmationModal());
	}
);

export const onCancel = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('massImport/onCancel', async (_, { dispatch }) => {
	dispatch(push('/'));
});

export interface IMassImportPageState {
	isMassImportInProgress: boolean;
	isSaveInProgress: boolean;
	canUploadBulkApplyFiles: boolean;
	bulkApplyBlueprintIds: number[];
	showBulkApplyConfirmationModal: boolean;
	bulkApplyConfirmationModalFile: File;
	bulkApplyConfirmationModalBlueprintId: number;
	bulkApplyConfirmationModalApplyChanges: boolean;
	bulkApplyConfirmationModalAcknowledgementChecked: boolean;
}

const initialState: IMassImportPageState = {
	isMassImportInProgress: true,
	isSaveInProgress: false,
	canUploadBulkApplyFiles: false,
	bulkApplyBlueprintIds: [],
	showBulkApplyConfirmationModal: false,
	bulkApplyConfirmationModalFile: null,
	bulkApplyConfirmationModalBlueprintId: null,
	bulkApplyConfirmationModalApplyChanges: null,
	bulkApplyConfirmationModalAcknowledgementChecked: false,
};

export const massImportSlice = createSlice({
	name: 'MassImport',
	initialState,
	reducers: {
		openBulkApplyConfirmationModal: (state, action) => {
			state.showBulkApplyConfirmationModal = true;
			state.bulkApplyConfirmationModalFile = action.payload.file;
			state.bulkApplyConfirmationModalBlueprintId = action.payload.blueprintId;
			state.bulkApplyConfirmationModalApplyChanges =
				action.payload.applyChanges;
		},
		closeBulkApplyConfirmationModal: (state) => {
			state.showBulkApplyConfirmationModal = false;
			state.bulkApplyConfirmationModalFile = null;
			state.bulkApplyConfirmationModalBlueprintId = null;
			state.bulkApplyConfirmationModalApplyChanges = null;
			state.bulkApplyConfirmationModalAcknowledgementChecked = false;
		},
		acknowledgeBulkApplyConfirmationModalCheckboxChanged: (state, action) => {
			state.bulkApplyConfirmationModalAcknowledgementChecked =
				action.payload.checked;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(onPageLoad.fulfilled, (state) => {
			state.canUploadBulkApplyFiles = User.isDssiSiteType();
		});

		builder.addCase(fetchMassImportInProgress.fulfilled, (state, action) => {
			state.isMassImportInProgress = action.payload;
		});

		builder.addCase(submitMassImportRequest.pending, (state) => {
			state.isSaveInProgress = true;
		});
		builder.addCase(submitMassImportRequest.fulfilled, (state) => {
			state.isSaveInProgress = false;
		});
		builder.addCase(submitMassImportRequest.rejected, (state) => {
			state.isSaveInProgress = false;
		});

		builder.addCase(submitBulkApplyRequest.pending, (state) => {
			state.isSaveInProgress = true;
		});
		builder.addCase(submitBulkApplyRequest.fulfilled, (state) => {
			state.isSaveInProgress = false;
		});
		builder.addCase(submitBulkApplyRequest.rejected, (state) => {
			state.isSaveInProgress = false;
		});

		builder.addCase(
			BlueprintsSlice.fetchBlueprints.fulfilled,
			(state, action) => {
				if (User.isDssiSiteType()) {
					state.bulkApplyBlueprintIds = action.payload.results.map(
						(bp) => bp.id
					);
				} else {
					state.bulkApplyBlueprintIds = [];
				}
			}
		);
	},
});
